/**
 * Gemini Onboarding Contacts — executive contacts (CEO/COO/CFO/CNO…)
 * captured during underwriting to feed the Gemini onboarding workflow
 * (OR-1326 / backend OR-1312).
 *
 * Model: contacts live in a GLOBAL pool (`/v2/contacts/`, deduped by
 * email) and are attached to / detached from a submission via
 * `/v2/submissions/{id}/onboarding-contacts/`. The FE never PATCHes a
 * pool contact — editing a selected contact's fields forks a new one
 * (same convention as the Brokerage modal), so shared records are
 * never mutated from one submission.
 */

export interface OnboardingContactWire {
    id: string;
    email: string;
    name: string;
    /** Canonical enum value ("Chief Executive Officer", …) or free text. */
    job_title: string;
    phone: string | null;
    created_at: string;
    updated_at: string;
}

/** FE-facing contact row. `jobTitle` keeps the wire value verbatim —
 *  the short display code (CEO/COO/…) is derived at render time. */
export interface OnboardingContact {
    id: string;
    email: string;
    name: string;
    jobTitle: string;
    phone: string;
}

export interface OnboardingContactCreatePayload {
    email: string;
    name: string;
    job_title: string;
    phone?: string | null;
}

/** The four canonical titles: the backend enum stores the long form,
 *  the UI shows the short code (design, OR-1310). Any other string is
 *  a custom title and passes through unchanged in both directions. */
const CANONICAL_JOB_TITLES = [
    { short: "CEO", long: "Chief Executive Officer" },
    { short: "COO", long: "Chief Operating Officer" },
    { short: "CFO", long: "Chief Financial Officer" },
    { short: "CNO", long: "Chief Nursing Officer" },
] as const;

/** Dropdown presets (short codes), in the design's order. */
export const JOB_TITLE_PRESETS = CANONICAL_JOB_TITLES.map(({ short }) => ({ id: short, label: short }));

/** UI value → wire value: short code becomes the canonical enum long
 *  form; anything else (custom title) is sent as typed. */
export function jobTitleToWire(value: string): string {
    const hit = CANONICAL_JOB_TITLES.find((t) => t.short.toLowerCase() === value.trim().toLowerCase());
    return hit ? hit.long : value.trim();
}

/** Wire value → display: canonical long form collapses to the short
 *  code; custom titles render verbatim. */
export function displayJobTitle(wire: string): string {
    const hit = CANONICAL_JOB_TITLES.find((t) => t.long.toLowerCase() === wire.trim().toLowerCase());
    return hit ? hit.short : wire;
}

export function wireToOnboardingContact(wire: OnboardingContactWire): OnboardingContact {
    return {
        id: wire.id,
        email: wire.email,
        name: wire.name,
        jobTitle: wire.job_title,
        phone: wire.phone ?? "",
    };
}
